import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, ChevronLeft, ChevronRight, UserPlus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { DateField, EmailField, PhoneField } from "../components/ui/FormFields";
import { PositionPicker } from "../components/ui/PositionPicker";
import { api, ApiError } from "../lib/api";
import type { PositionListItem } from "../lib/api";
import { useLegalEntityId } from "../lib/legalEntity";

// 4-step Add Employee wizard (M11.5).
//
// Step 1 — Identity + Personal. Required: names (EN+AR),
//          is_saudi_national, national ID/Iqama, DOB, gender, nationality,
//          hire_date.
// Step 2 — Position assignment (optional). Pick an open Position from
//          the M10.6 list endpoint.
// Step 3 — Contract terms. Stub — free-text notes until M12.
// Step 4 — Confirm.
//
// LE id from ?le=<uuid> + customer_id from ?customer=<uuid> query params
// (same convention as the other M10/M11 pages; the LE/customer pickers
// land in a later milestone).

// Form-friendly nationality slugs → ISO 3166-1 alpha-2 codes the backend
// stores. Keep in sync with the <select> options below. Restricted to
// the most common KSA workforce nationalities for v1.
const NATIONALITY_SLUG_TO_ISO: Record<string, string> = {
  saudi: "SA",
  indian: "IN",
  pakistani: "PK",
  filipino: "PH",
  egyptian: "EG",
  bangladeshi: "BD",
  yemeni: "YE",
  sudanese: "SD",
  american: "US",
  british: "GB",
};

type FormState = {
  // Step 1
  first_name_en: string;
  first_name_ar: string;
  middle_name_en: string;
  middle_name_ar: string;
  last_name_en: string;
  last_name_ar: string;
  is_saudi_national: boolean;
  national_id_or_iqama: string;
  date_of_birth: string;
  gender: string;
  nationality: string;
  marital_status: string;
  work_email: string;
  phone_number: string;
  hire_date: string;
  employee_code: string;
  // Step 2
  position_id: string;
  assignment_start: string;
  // Step 3 — Contract (M12.8). Empty strings → server omits contract.
  contract_notes: string;
  contract_period: string;
  probation_period_days: string;
  basic_salary: string;
  housing_allowance: string;
  transportation_allowance: string;
  food_allowance: string;
  salary_currency: string;
};

const STEPS = [
  { key: 1, labelKey: "employee_new.step1_label" },
  { key: 2, labelKey: "employee_new.step2_label" },
  { key: 3, labelKey: "employee_new.step3_label" },
  { key: 4, labelKey: "employee_new.step4_label" },
] as const;

function StepIndicator({ active }: { active: number }) {
  const { t } = useTranslation();
  return (
    <ol className="flex items-center gap-2">
      {STEPS.map(({ key, labelKey }, i) => (
        <li key={key} className="flex items-center gap-2">
          <div
            className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${
              active === key
                ? "bg-[linear-gradient(135deg,var(--brand-blue),var(--brand-purple))] text-white"
                : active > key
                  ? "bg-state-approved text-white"
                  : "bg-surface-sunken text-text-tertiary"
            }`}
          >
            {active > key ? <Check className="h-4 w-4" /> : key}
          </div>
          <span
            className={`text-sm ${
              active === key
                ? "font-semibold text-text-primary"
                : "text-text-tertiary"
            }`}
          >
            {t(labelKey)}
          </span>
          {i < STEPS.length - 1 && (
            <ChevronRight className="h-4 w-4 text-border-default rtl:rotate-180" />
          )}
        </li>
      ))}
    </ol>
  );
}

function Step1IdentityPersonal({
  form,
  setForm,
}: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}) {
  const { t } = useTranslation();
  const update = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  return (
    <div className="grid grid-cols-2 gap-4">
      <label className="block text-sm">
        <span className="font-medium text-slate-700">{t("employee_new.employee_code")} *</span>
        <input
          required
          value={form.employee_code}
          onChange={update("employee_code")}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
        />
      </label>
      <DateField
        label={t("employee_new.hire_date")}
        required
        value={form.hire_date}
        onChange={update("hire_date")}
        max={new Date().toISOString().slice(0, 10)}
      />

      <label className="block text-sm">
        <span className="font-medium text-slate-700">{t("employee_new.first_name_en")} *</span>
        <input
          required
          value={form.first_name_en}
          onChange={update("first_name_en")}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">{t("employee_new.first_name_ar")} *</span>
        <input
          required
          dir="rtl"
          value={form.first_name_ar}
          onChange={update("first_name_ar")}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">{t("employee_new.middle_name_en")}</span>
        <input
          value={form.middle_name_en}
          onChange={update("middle_name_en")}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">{t("employee_new.middle_name_ar")}</span>
        <input
          dir="rtl"
          value={form.middle_name_ar}
          onChange={update("middle_name_ar")}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">{t("employee_new.last_name_en")} *</span>
        <input
          required
          value={form.last_name_en}
          onChange={update("last_name_en")}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">{t("employee_new.last_name_ar")} *</span>
        <input
          required
          dir="rtl"
          value={form.last_name_ar}
          onChange={update("last_name_ar")}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>

      <label className="block text-sm">
        <span className="font-medium text-slate-700">{t("employee_new.saudi_national")}</span>
        <select
          value={form.is_saudi_national ? "yes" : "no"}
          onChange={(e) =>
            setForm({
              ...form,
              is_saudi_national: e.target.value === "yes",
            })
          }
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="yes">{t("employee_new.yes_saudi")}</option>
          <option value="no">{t("employee_new.no_iqama")}</option>
        </select>
      </label>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">
          {form.is_saudi_national
            ? `${t("employee_new.national_id")} *`
            : `${t("employee_new.iqama_number")} *`}
        </span>
        <input
          required
          value={form.national_id_or_iqama}
          onChange={update("national_id_or_iqama")}
          placeholder={t("employee_new.ten_digits")}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
        />
      </label>

      <DateField
        label={t("employee_new.date_of_birth")}
        required
        value={form.date_of_birth}
        onChange={update("date_of_birth")}
        max={new Date().toISOString().slice(0, 10)}
      />
      <label className="block text-sm">
        <span className="font-medium text-slate-700">{t("employee_new.gender")} *</span>
        <select
          required
          value={form.gender}
          onChange={update("gender")}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">—</option>
          <option value="M">{t("employee_new.male")}</option>
          <option value="F">{t("employee_new.female")}</option>
        </select>
      </label>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">{t("employee_new.nationality_iso")} *</span>
        <select
          required
          value={form.nationality}
          onChange={update("nationality")}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">—</option>
          <option value="saudi">{t("employee_new.nat_saudi", "Saudi")}</option>
          <option value="indian">{t("employee_new.nat_indian", "Indian")}</option>
          <option value="pakistani">{t("employee_new.nat_pakistani", "Pakistani")}</option>
          <option value="filipino">{t("employee_new.nat_filipino", "Filipino")}</option>
          <option value="egyptian">{t("employee_new.nat_egyptian", "Egyptian")}</option>
          <option value="bangladeshi">{t("employee_new.nat_bangladeshi", "Bangladeshi")}</option>
          <option value="yemeni">{t("employee_new.nat_yemeni", "Yemeni")}</option>
          <option value="sudanese">{t("employee_new.nat_sudanese", "Sudanese")}</option>
          <option value="american">{t("employee_new.nat_american", "American")}</option>
          <option value="british">{t("employee_new.nat_british", "British")}</option>
        </select>
      </label>

      <label className="block text-sm">
        <span className="font-medium text-slate-700">{t("employee_new.marital_status")}</span>
        <select
          value={form.marital_status}
          onChange={update("marital_status")}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">—</option>
          <option value="single">{t("employee_new.single")}</option>
          <option value="married">{t("employee_new.married")}</option>
          <option value="divorced">{t("employee_new.divorced")}</option>
          <option value="widowed">{t("employee_new.widowed")}</option>
        </select>
      </label>
      <EmailField
        label={t("employee_new.work_email")}
        value={form.work_email}
        onChange={update("work_email")}
      />
      <PhoneField
        label={t("employee_new.phone_e164")}
        value={form.phone_number}
        onChange={update("phone_number")}
      />
    </div>
  );
}

function Step2Position({
  form,
  setForm,
  legalEntityId,
}: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  legalEntityId: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-slate-600">{t("employee_new.step2_intro")}</p>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">{t("employee_new.position_label")}</span>
        <div className="mt-1">
          <PositionPicker
            legalEntityId={legalEntityId}
            value={form.position_id || null}
            status="open"
            onChange={(p) =>
              setForm({ ...form, position_id: p?.id ?? "" })
            }
            placeholder={t("employee_new.position_search_placeholder")}
          />
        </div>
      </label>
      {form.position_id && (
        <DateField
          label={t("employee_new.assignment_start")}
          value={form.assignment_start}
          onChange={(e) =>
            setForm({ ...form, assignment_start: e.target.value })
          }
          helper={t("employee_new.assignment_start_hint")}
        />
      )}
    </div>
  );
}

function Step3Contract({
  form,
  setForm,
}: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}) {
  const { t } = useTranslation();
  const update =
    (k: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm({ ...form, [k]: e.target.value });

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-slate-600">{t("employee_new.step3_intro")}</p>

      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm">
          <span className="font-medium text-slate-700">{t("employee_new.basic_salary")}</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.basic_salary}
            onChange={update("basic_salary")}
            placeholder="8000.00"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-700">{t("employee_new.currency")}</span>
          <select
            value={form.salary_currency}
            onChange={update("salary_currency")}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="SAR">SAR</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="AED">AED</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-700">{t("employee_new.housing_allowance")}</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.housing_allowance}
            onChange={update("housing_allowance")}
            placeholder="0.00"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-700">{t("employee_new.transportation_allowance")}</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.transportation_allowance}
            onChange={update("transportation_allowance")}
            placeholder="0.00"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-700">{t("employee_new.food_allowance")}</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.food_allowance}
            onChange={update("food_allowance")}
            placeholder="0.00"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-700">{t("employee_new.contract_period")}</span>
          <select
            value={form.contract_period}
            onChange={update("contract_period")}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="unlimited">{t("employee_new.period_unlimited")}</option>
            <option value="limited">{t("employee_new.period_limited")}</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-700">{t("employee_new.probation_days")}</span>
          <input
            type="number"
            min="0"
            max="365"
            value={form.probation_period_days}
            onChange={update("probation_period_days")}
            placeholder="90"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
          />
        </label>
      </div>

      <label className="block text-sm">
        <span className="font-medium text-slate-700">{t("employee_new.contract_notes")}</span>
        <textarea
          rows={3}
          value={form.contract_notes}
          onChange={(e) =>
            setForm({ ...form, contract_notes: e.target.value })
          }
          placeholder={t("employee_new.contract_notes_placeholder")}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
    </div>
  );
}

function Step4Confirm({
  form,
  positions,
}: {
  form: FormState;
  positions: PositionListItem[];
}) {
  const { t } = useTranslation();
  const pos = form.position_id
    ? positions.find((p) => p.id === form.position_id)
    : null;
  return (
    <div className="flex flex-col gap-2 text-sm">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <p className="font-semibold">
          {form.first_name_en} {form.middle_name_en} {form.last_name_en}
        </p>
        <p dir="rtl" className="text-slate-600">
          {form.first_name_ar} {form.middle_name_ar} {form.last_name_ar}
        </p>
        <p className="mt-2 text-slate-700">
          {form.is_saudi_national
            ? t("employee_new.confirm_label_saudi")
            : t("employee_new.confirm_label_non_saudi")}{" "}
          · {form.gender} · {form.nationality} · DOB {form.date_of_birth}
        </p>
        <p className="text-slate-700">
          {form.is_saudi_national
            ? t("employee_new.national_id")
            : t("employee_new.iqama_number")}
          : <code>{form.national_id_or_iqama}</code>
        </p>
        <p className="mt-2 text-slate-700">
          {t("employee_new.confirm_code")}{" "}
          <code>{form.employee_code}</code> ·{" "}
          {t("employee_new.confirm_hire")} {form.hire_date}
        </p>
        {form.work_email && (
          <p className="text-slate-700">
            {t("employee_new.confirm_email")}: {form.work_email}
          </p>
        )}
      </div>
      <div className="rounded-lg border border-slate-200 p-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {t("employee_new.confirm_position")}
        </p>
        {pos ? (
          <p className="mt-1">
            {pos.position_number_display} — {pos.title_en}
            <span className="ms-2 text-xs text-slate-500">
              {t("employee_new.confirm_position_starts", {
                date: form.assignment_start || form.hire_date,
              })}
            </span>
          </p>
        ) : (
          <p className="mt-1 italic text-slate-500">
            {t("employee_new.confirm_no_position")}
          </p>
        )}
      </div>
      {form.basic_salary ? (
        <div className="rounded-lg border border-slate-200 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {t("employee_new.confirm_contract_section")}
          </p>
          <p className="mt-1 text-slate-700">
            {t("employee_new.confirm_basic_salary", {
              amount: form.basic_salary,
              currency: form.salary_currency,
            })}
            {form.housing_allowance && (
              <span>
                {" "}
                ·{" "}
                {t("employee_new.confirm_housing_inline", {
                  amount: form.housing_allowance,
                })}
              </span>
            )}
            {form.transportation_allowance && (
              <span>
                {" "}
                ·{" "}
                {t("employee_new.confirm_transport_inline", {
                  amount: form.transportation_allowance,
                })}
              </span>
            )}
          </p>
          <p className="text-slate-700">
            {t("employee_new.confirm_period_probation", {
              period:
                form.contract_period === "limited"
                  ? t("employee_new.period_limited")
                  : t("employee_new.period_unlimited"),
              days: form.probation_period_days,
            })}
          </p>
          {form.contract_notes && (
            <p className="mt-1 whitespace-pre-wrap italic text-slate-600">
              {form.contract_notes}
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 p-4 text-sm italic text-slate-500">
          {t("employee_new.confirm_no_contract")}
        </div>
      )}
    </div>
  );
}

export function EmployeeNewPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  // Customer id auto-resolves to the tenant's first Customer row, same
  // way EmployeeListPage picks it. URL ?customer=<uuid> still wins so
  // a power user can deep-link a non-default customer in a future
  // multi-customer-per-tenant world.
  const params = new URLSearchParams(window.location.search);
  const urlCustomer = params.get("customer") ?? "";
  const customersQ = useQuery({
    queryKey: ["customers"],
    queryFn: () => api.listCustomers(),
    enabled: !urlCustomer,
  });
  const customer =
    urlCustomer || customersQ.data?.[0]?.id || "";

  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({
    first_name_en: "",
    first_name_ar: "",
    middle_name_en: "",
    middle_name_ar: "",
    last_name_en: "",
    last_name_ar: "",
    is_saudi_national: true,
    national_id_or_iqama: "",
    date_of_birth: "",
    gender: "",
    nationality: "saudi",
    marital_status: "",
    work_email: "",
    phone_number: "",
    hire_date: new Date().toISOString().slice(0, 10),
    employee_code: "",
    position_id: "",
    assignment_start: "",
    contract_notes: "",
    contract_period: "unlimited",
    probation_period_days: "90",
    basic_salary: "",
    housing_allowance: "",
    transportation_allowance: "",
    food_allowance: "",
    salary_currency: "SAR",
  });

  const positionsQ = useQuery({
    queryKey: ["positions", le, "open"],
    queryFn: () => api.listPositions(le, { status: "open" }),
    enabled: !!le,
  });

  const mutation = useMutation({
    mutationFn: () =>
      api.createEmployee({
        customer_id: customer,
        legal_entity_id: le,
        employee_code: form.employee_code,
        first_name_en: form.first_name_en,
        first_name_ar: form.first_name_ar,
        middle_name_en: form.middle_name_en || null,
        middle_name_ar: form.middle_name_ar || null,
        last_name_en: form.last_name_en,
        last_name_ar: form.last_name_ar,
        is_saudi_national: form.is_saudi_national,
        national_id_or_iqama: form.national_id_or_iqama,
        date_of_birth: form.date_of_birth,
        // ISO 5218-style form codes (M/F) → backend enum values (male/female).
        gender:
          form.gender === "M"
            ? "male"
            : form.gender === "F"
              ? "female"
              : form.gender,
        // Form stores nationality slug; backend expects ISO 3166-1 alpha-2.
        nationality: NATIONALITY_SLUG_TO_ISO[form.nationality] ?? form.nationality,
        hire_date: form.hire_date,
        marital_status: form.marital_status || null,
        work_email: form.work_email || null,
        phone_number: form.phone_number || null,
        position_id: form.position_id || null,
        assignment_start: form.assignment_start || null,
        contract_notes: form.contract_notes || null,
        contract_period: form.contract_period || null,
        probation_period_days: form.probation_period_days
          ? Number(form.probation_period_days)
          : null,
        basic_salary: form.basic_salary || null,
        housing_allowance: form.housing_allowance || null,
        transportation_allowance: form.transportation_allowance || null,
        food_allowance: form.food_allowance || null,
        salary_currency: form.salary_currency || "SAR",
      }),
    onSuccess: (data) => {
      navigate(`/people/employees/${data.id}`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setErrorMsg(e.detail);
      else setErrorMsg(String(e));
    },
  });

  if (!le || (!urlCustomer && customersQ.isLoading)) return <Loading />;
  if (!customer) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
        {t("employee_new.no_customer_warning")}
      </div>
    );
  }
  if (positionsQ.isLoading) return <Loading />;
  if (positionsQ.error)
    return <ErrorPanel message={(positionsQ.error as Error).message} />;

  const positions = positionsQ.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="flex items-center gap-2 text-2xl font-bold text-text-primary">
        <UserPlus className="h-6 w-6 text-text-tertiary" />
        {t("employee_new.page_title")}
      </h1>

      <StepIndicator active={step} />

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        {step === 1 && <Step1IdentityPersonal form={form} setForm={setForm} />}
        {step === 2 && (
          <Step2Position form={form} setForm={setForm} legalEntityId={le} />
        )}
        {step === 3 && <Step3Contract form={form} setForm={setForm} />}
        {step === 4 && <Step4Confirm form={form} positions={positions} />}
      </div>

      {errorMsg && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {errorMsg}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4 rtl:rotate-180" />{" "}
          {t("employee_new.back")}
        </button>
        {step < 4 ? (
          <button
            type="button"
            onClick={() => setStep(step + 1)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:opacity-90"
          >
            {t("employee_new.next")}{" "}
            <ChevronRight className="h-4 w-4 rtl:rotate-180" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <Check className="h-4 w-4" />
            {mutation.isPending
              ? t("employee_new.creating")
              : t("employee_new.create_employee")}
          </button>
        )}
      </div>
    </div>
  );
}
